import React, { useState, useEffect } from "react";
import axios from "axios";
import {normalizeProductName} from "../helpers/utils";
import dayjs from "dayjs";
import "dayjs/locale/uk";
import DatePicker, { registerLocale } from "react-datepicker";
import { uk } from "date-fns/locale";
import {ACCOUNTS, BOT_TOKEN, CHAT_ID_AT, FB_ACCOUNTS, TIMEZONE} from "../constans";
import { getPrReportSheetTitleForMonth } from "../helpers/prReportGoogleSheet";
import * as bootstrap from "bootstrap";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import "./ProductsMatrix.scss";

registerLocale("uk", uk);
dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.locale("uk");

/** Для fb_video/video не підставляти media_url у <img> — це відео, прев’ю лише з thumbnail_url. */
function creativeTableThumbnailSrc(ad) {
    const isVideo =
        ad.media_type === "fb_video" || ad.media_type === "video";
    if (isVideo) {
        return (
            ad.thumbnail_url ||
            ad.carousel?.[0]?.thumbnail_url ||
            ""
        );
    }
    return (
        ad.thumbnail_url ||
        ad.media_url ||
        ad.carousel?.[0]?.thumbnail_url ||
        ad.carousel?.[0]?.media_url ||
        ""
    );
}

/** effective_status / status з Facebook Marketing API */
const FB_AD_STATUS_UK = {
    ACTIVE: "Активне",
    PAUSED: "На паузі",
    ARCHIVED: "Архів",
    DELETED: "Видалено",
    IN_PROCESS: "Обробка",
    WITH_ISSUES: "З проблемами",
    DISAPPROVED: "Відхилено",
    PENDING_REVIEW: "На перевірці",
    PREAPPROVED: "Попередньо схвалено",
    PENDING_BILLING_INFO: "Очікує біллінг",
    CAMPAIGN_PAUSED: "Кампанія на паузі",
    ADSET_PAUSED: "Група оголошень на паузі",
};

function MatrixCreativeStatus({ ad }) {
    const raw = ad.effective_status || ad.status;
    if (!raw) {
        return <span className="text-muted small" title="Після наступної синхронізації з FB">—</span>;
    }
    const label = FB_AD_STATUS_UK[raw] || raw.replace(/_/g, " ");
    if (raw === "ACTIVE") {
        return <span className="badge bg-success">{label}</span>;
    }
    if (raw === "PAUSED" || raw === "CAMPAIGN_PAUSED" || raw === "ADSET_PAUSED") {
        return <span className="badge bg-warning text-dark">{label}</span>;
    }
    if (raw === "DISAPPROVED" || raw === "WITH_ISSUES") {
        return <span className="badge bg-danger">{label}</span>;
    }
    if (raw === "ARCHIVED" || raw === "DELETED") {
        return <span className="badge bg-dark">{label}</span>;
    }
    return <span className="badge bg-secondary">{label}</span>;
}

export default function ProductsMatrix({user}) {

    const defaultEnd = dayjs().tz(TIMEZONE).subtract(1, "day");
    const defaultStart = defaultEnd.subtract(2, "day"); // 3 дня

    const [from, setFrom] = useState(defaultStart.toDate());
    const [to, setTo] = useState(defaultEnd.toDate());

    const [matrix,setMatrix] = useState([]);
    const [days,setDays] = useState([]);
    const [source, setSource] = useState("all"); // all | site | ig
    const [modalContent, setModalContent] = useState(null);

    const [sortField, setSortField] = useState("total");
    const [sortDir, setSortDir] = useState("desc");

    const [currency, setCurrency] = useState("USD"); // "USD" | "UAH"
    const [monthSheetSyncing, setMonthSheetSyncing] = useState(false);

    const [products,setProducts] = useState([]);
    const [baseNames,setBaseNames] = useState({});
    const [autoNames,setAutoNames] = useState({});
    const [usdSellRate, setUsdSellRate] = useState(null);
    const names = {...baseNames, ...autoNames};
    const [summaries, setSummaries] = useState([]);
    const [tableSortField, setTableSortField] = useState("roi");
    const [tableSortDir, setTableSortDir] = useState("desc");
    const [expandedRows, setExpandedRows] = useState({});
    const [activePeriod, setActivePeriod] = useState("3days");
    const [loading, setLoading] = useState(false);



    const [viewMode, setViewMode] = useState("spent");
    const [creatives, setCreatives] = useState([]);
    const [openedCatalogAdId, setOpenedCatalogAdId] = useState(null);
    const [catalogStats, setCatalogStats] = useState([]);

    const isAdmin = user?.role === "admin"

    const toggleRow = (pid) => {
        setExpandedRows(prev => ({
            ...prev,
            [pid]: !prev[pid]
        }));
    };

    const renderSortArrow = (field) => {
        if (tableSortField !== field) return null;
        return tableSortDir === "asc" ? " 🔼" : " 🔽";
    };


    useEffect(() => {
        const loadCreatives = async () => {
            const res = await axios.get("/api/reports/product-creatives-range", {
                params: {
                    from: dayjs(from).format("YYYY-MM-DD"),
                    to: dayjs(to).format("YYYY-MM-DD"),
                }
            });

            setCreatives(res.data);
        };

        loadCreatives();
    }, [from, to]);

    const creativesByProduct = React.useMemo(() => {

        const map = {};

        creatives.forEach(ad => {
            ad.selected_products?.forEach(pid => {

                if (!map[pid]) {
                    map[pid] = [];
                }

                map[pid].push(ad);
            });
        });

        return map;

    }, [creatives]);



    const revenueUSD = (cell) => {
        const uah = pick(cell, "revenue"); // revenue в UAH
        return usdSellRate ? uah / usdSellRate : 0;
    };

    useEffect(() => {
        const fetchSummary = async () => {
            const start = dayjs(from).format("YYYY-MM-DD");
            const end = dayjs(to).format("YYYY-MM-DD");

            try {
                const res = await fetch(`/api/summary?start=${start}&end=${end}`);
                const data = await res.json();
                setSummaries(data);
            } catch (e) {
                console.error("Помилка завантаження summary", e);
            }
        };

        fetchSummary();
    }, [from, to]);

    const allOrders = summaries.flatMap(s => s.orders || []);

    const filteredOrders = allOrders.filter(order => {

        const hasBarterTag = order.tags?.some(tag => tag.id === 1);
        const hasWholesaleTag = order.tags?.some(tag => tag.id === 4);

        if (hasBarterTag || hasWholesaleTag) return false;

        // 🔥 ФИЛЬТР ПО ИСТОЧНИКУ
        if (source === "site" && order.source_id !== 9) return false;

        if (source === "ig" && ![1,4].includes(order.source_id)) return false;

        return true;
    });



    const sortedMatrix = React.useMemo(()=>{

        const calcTotal = (row) => {
            const totalSpent = row.days.reduce((s,x)=> s + pick(x, "spend"), 0); // USD
            const totalSold  = row.days.reduce((s,x)=> s + pick(x, "qty"), 0);
            const totalRevenueUSD = row.days.reduce((s,x)=> s + revenueUSD(x), 0);
            const totalCostUSD = row.days.reduce((s,x)=> s + pick(x, "cost"), 0);  // ✅ USD

            const totalProfit = totalRevenueUSD - totalCostUSD - totalSpent;

            switch(viewMode){
                case "sold":    return totalSold;
                case "spent":   return totalSpent;
                case "revenue": return totalRevenueUSD; // ✅ revenue в USD
                case "profit":  return totalProfit;
                case "roas":    return totalSpent > 0 ? totalRevenueUSD / totalSpent : 0;
                case "cpa":     return totalSold  > 0 ? totalSpent  / totalSold  : 0;
                default:        return totalSold;
            }
        };



        const arr = matrix.map(r => ({
            ...r,
            total: calcTotal(r)
        }));

        arr.sort((a,b)=> sortDir==="asc" ? a.total - b.total : b.total - a.total);

        return arr;

    }, [matrix, viewMode, sortDir, source, usdSellRate]);

    async function loadProducts(){
        const res = await axios.get("/api/products/unique");
        setProducts(res.data);

        const map = {};
        res.data.forEach(p=>{ map[p.product_id] = p.name });
        setBaseNames(map);
    }

    useEffect(()=>{ loadProducts() },[]);

    useEffect(() => {
        const fetchRates = async () => {
            try {
                const res = await fetch("/api/exchange-rates");
                const data = await res.json();

                if (!Array.isArray(data)) {
                    console.warn("⚠️ Exchange rates response is not array:", data);
                    return;
                }

                const usd = data.find(c => c.currencyCodeA === 840);
                if (usd?.rateSell) {
                    setUsdSellRate(usd.rateSell);
                } else {
                    console.warn("⚠️ USD rate not found", data);
                }
            } catch (err) {
                console.error("❌ Failed to fetch exchange rates", err);
            }
        };

        fetchRates();
    }, []);
    const DEFAULT_USD_RATE = 42.5; // временно / вручную

    useEffect(() => {
        if (!usdSellRate) {
            console.warn("⚠️ Using fallback USD rate:", DEFAULT_USD_RATE);
            setUsdSellRate(DEFAULT_USD_RATE);
        }
    }, [usdSellRate]);



    useEffect(() => {
        const missing = matrix
            .map(row => row.pid)
            .filter(pid => !names[pid]);

        if (missing.length) {
            console.warn("❗ Товари без назви:", missing);
        }
    }, [matrix, names]);




    function pick(x, field) {
        if (!x) return 0;

        const val =
            source === "all"
                ? (x.site?.[field] || 0) + (x.ig?.[field] || 0)
                : x[source]?.[field] || 0;

        if (field === "cost") {
            return usdSellRate ? val / usdSellRate : 0;
        }

        return val;
    }


    async function load() {
        try {
            setLoading(true);

            const res = await axios.get("/api/reports/products-range", {
                params: {
                    from: from.toLocaleDateString("sv-SE"),
                    to:   to.toLocaleDateString("sv-SE"),
                },
            });

            // ✅ поддержка обоих вариантов ответа сервера
            const serverDays = res.data.days || [];
            const serverMatrix =
                res.data.matrixLoad ||
                res.data.matrix ||
                {};
            setDays(serverDays);

            const rows = Object.entries(serverMatrix).map(([pid, daysMap]) => ({
                pid: Number(pid),
                name: names[pid] ? normalizeProductName(names[pid]) : null,
                days: serverDays.map(date =>
                        daysMap?.[date] || {
                            site: { spend: 0, qty: 0, revenue: 0 },
                            ig:   { spend: 0, qty: 0, revenue: 0 },
                        }
                ),
            }));

            setMatrix(rows);
        } catch (err) {
            console.error("🔴 LOAD FAILED:", err);
        } finally {
            setLoading(false);
        }
    }

    async function syncGoogleSheetMonthData() {
        setMonthSheetSyncing(true);
        try {
            const nowKyiv = dayjs().tz(TIMEZONE);
            const month = nowKyiv.startOf("month").format("YYYY-MM-DD");
            const sheetTitle = getPrReportSheetTitleForMonth(nowKyiv.toDate());
            const headers = { "Content-Type": "application/json" };
            const bodyBase = JSON.stringify({ month, sheetTitle });

            const res = await fetch("/api/google-sheets/sync-test-report-data", {
                method: "POST",
                headers,
                credentials: "same-origin",
                body: bodyBase,
            });
            const text = await res.text();
            let data;
            try {
                data = JSON.parse(text);
            } catch {
                throw new Error(text.slice(0, 200));
            }
            if (!res.ok || !data.success) {
                throw new Error(data?.message || `HTTP ${res.status}`);
            }

            const resLive = await fetch("/api/google-sheets/sync-test-report-today-live", {
                method: "POST",
                headers,
                credentials: "same-origin",
                body: bodyBase,
            });
            const textLive = await resLive.text();
            let dataLive;
            try {
                dataLive = JSON.parse(textLive);
            } catch {
                throw new Error(textLive.slice(0, 200));
            }
            if (!resLive.ok || !dataLive.success) {
                throw new Error(dataLive?.message || `HTTP ${resLive.status}`);
            }
            const skipped = dataLive.result?.skipped;
            const liveNote = skipped
                ? `Рядок «сьогодні» пропущено (${dataLive.result?.reason || "—"}).`
                : "Рядок «сьогодні» оновлено з live CRM/FB.";
            window.alert(`Google «${sheetTitle}»: повний місяць з БД оновлено. ${liveNote}`);
        } catch (e) {
            console.error(e);
            window.alert(e?.message || "Не вдалося оновити Google Sheet");
        } finally {
            setMonthSheetSyncing(false);
        }
    }

    const setLastDays = (count) => {
        const end = dayjs().tz(TIMEZONE).subtract(1, "day");
        const start = end.subtract(count - 1, "day");

        setFrom(start.toDate());
        setTo(end.toDate());
    };

    const formatMoneySimple = (value) => {
        if (!value) return "";

        if (currency === "USD") {
            return `$${value.toFixed(2)}`;
        }

        if (currency === "UAH") {
            return `${(value * usdSellRate).toFixed(0)} ₴`;
        }

        return value;
    };


    const formatMoney = (value, type) => {
        if (value === null || value === undefined || value === 0) return "";

        // profit/spend у нас в USD, revenue мы тоже теперь можем показывать в USD логично
        if (currency === "USD") {
            if (type === "spend" || type === "profit") return `$${value.toFixed(2)}`;
            if (type === "revenue") return `$${value.toFixed(2)}`;
        }

        if (currency === "UAH") {
            if (type === "spend" || type === "profit") return `${(value * usdSellRate).toFixed(0)} ₴`;
            if (type === "revenue") return `${(value * usdSellRate).toFixed(0)} ₴`;
        }

        return value;
    };




    useEffect(() => {
        if (Object.keys(baseNames).length) load();
    }, [from, to, baseNames])

    const toggleSort = (field) => {
        if(sortField === field){
            setSortDir(prev => prev === "asc" ? "desc" : "asc");
        } else {
            setSortField(field);
            setSortDir("desc"); // по умолчанию — от большего к меньшему
        }
    };


    function formatCell(x) {
        const sold = pick(x, "qty");
        const spent = pick(x, "spend");        // USD
        const revUSD = revenueUSD(x);          // ✅ USD
        const costUSD = pick(x, "cost");       // ✅ USD
        const profitUSD = revUSD - costUSD - spent;

        if (viewMode === "sold") return sold || "";

        if (viewMode === "spent")
            return spent ? formatMoney(spent, "spend") : "";

        if (viewMode === "revenue")
            return revUSD ? formatMoney(revUSD, "revenue") : "";

        if (viewMode === "profit")
            return profitUSD ? formatMoney(profitUSD, "profit") : "";

        if (viewMode === "roas")
            return spent > 0 ? (revUSD / spent).toFixed(2) : "";

        if (viewMode === "cpa")
            return sold > 0 ? (spent / sold).toFixed(2) : "";

        return "";
    }



    const dayTotals = React.useMemo(() => {
        if (!matrix.length || !days.length) return [];

        return days.map((_, dayIndex) => {
            let qty = 0;
            let spend = 0;
            let revenue = 0;
            let cost = 0;

            matrix.forEach(row => {
                const cell = row.days[dayIndex];
                qty     += pick(cell, "qty");
                spend   += pick(cell, "spend");
                revenue += revenueUSD(cell);
                cost    += pick(cell, "cost");
            });

            switch (viewMode) {
                case "sold":
                    return qty;

                case "spent":
                    return spend;

                case "revenue":
                    return revenue;

                case "roas":
                    return spend > 0 ? (revenue / spend) : "";

                case "cpa":
                    return qty > 0 ? (spend / qty) : "";
                case "profit":
                    return revenue - cost - spend;

                default:
                    return qty;
            }
        });
    }, [matrix, days, source, viewMode]);


    const grandTotal = React.useMemo(() => {
        if (!dayTotals.length) return 0;

        let qty = 0;
        let spend = 0;
        let revenue = 0;
        let cost = 0;

        matrix.forEach(row => {
            row.days.forEach(cell => {
                qty     += pick(cell, "qty");
                spend   += pick(cell, "spend");     // USD
                revenue += revenueUSD(cell); // USD
                cost    += pick(cell, "cost");
            });
        });

        switch (viewMode) {
            case "sold": return qty;
            case "spent": return spend;
            case "revenue": return revenue;
            case "roas": return spend > 0 ? revenue / spend : 0;
            case "cpa": return qty > 0 ? spend / qty : 0;
            case "profit":
                return revenue - cost - spend;
            default: return 0;
        }
    }, [matrix, viewMode, source, usdSellRate]);


    const productSummary = React.useMemo(() => {
        return matrix.map(row => {

            const sold = row.days.reduce((s, d) => s + pick(d, "qty"), 0);
            const spend = row.days.reduce((s, d) => s + pick(d, "spend"), 0);
            const revenue = row.days.reduce((s, d) => s + revenueUSD(d), 0);
            const cost = row.days.reduce((s, d) => s + pick(d, "cost"), 0);

            const totalProfit = revenue - cost - spend;

            const avgSellPrice = sold > 0 ? revenue / sold : 0;
            const unitMargin = sold > 0 ? (revenue - cost) / sold : 0;
            const unitProfit = sold > 0 ? totalProfit / sold : 0;
            const unitCost = sold > 0 ? cost / sold : 0;

            const marginPercent = avgSellPrice > 0
                ? (unitMargin / avgSellPrice) * 100
                : 0;

            const roi = spend > 0
                ? ((totalProfit / spend) * 100)
                : 0;
            // 🔥 собираем метрики из креативов
            const ads = creativesByProduct[row.pid] || [];

            let totalClicks = 0;
            let totalImpressions = 0;

            ads.forEach(ad => {
                const productsCount = ad.selected_products?.length || 1;

                totalClicks += (ad.clicks || 0) / productsCount;
                totalImpressions += (ad.impressions || 0) / productsCount;
            });

// 🔥 считаем метрики
            const cpm = totalImpressions > 0
                ? (spend / totalImpressions) * 1000
                : 0;

            const cpc = totalClicks > 0
                ? spend / totalClicks
                : 0;

            const ctr = totalImpressions > 0
                ? (totalClicks / totalImpressions) * 100
                : 0;


            return {
                pid: row.pid,
                name: row.name,
                sold,
                spend,
                revenue,
                cost,
                totalProfit,
                avgSellPrice,
                unitCost,
                unitMargin,
                marginPercent,
                unitProfit,
                roi,
                clicks: totalClicks,
                impressions: totalImpressions,
                cpm,
                cpc,
                ctr
            };
        });
    }, [matrix, source, usdSellRate]);


    const withSalesAndSpend = productSummary.filter(r => r.sold > 0 && r.spend > 0);
    const onlySales = productSummary.filter(r => r.sold > 0 && r.spend === 0);
    const onlySpend = productSummary.filter(r => r.spend > 0 && r.sold === 0);


    const formatMoneyDynamic = (value) => {
        if (!value) return "";

        if (currency === "USD") {
            return `$${value.toFixed(2)}`;
        }

        return `${(value * usdSellRate).toFixed(0)} ₴`;
    };


    const hasAnyDataBySource = (row, source) => {
        return row.days.some(d => {
            if (source === "all") {
                const site = d?.site || {};
                const ig = d?.ig || {};

                return (
                    (site.qty || 0) + (ig.qty || 0) > 0 ||
                    (site.spend || 0) + (ig.spend || 0) > 0 ||
                    (site.revenue || 0) + (ig.revenue || 0) > 0
                );
            }

            const cell = d?.[source];
            if (!cell) return false;

            return (
                (cell.qty || 0) > 0 ||
                (cell.spend || 0) > 0 ||
                (cell.revenue || 0) > 0
            );
        });
    };

    const totalOrders = filteredOrders.length;

    const CHAT_ID = CHAT_ID_AT

    const sendToTelegram = async () => {
        try {

            const escapeHtml = (text = "") =>
                text
                    .replace(/&/g, "&amp;")
                    .replace(/</g, "&lt;")
                    .replace(/>/g, "&gt;");

            const indent = " ".repeat(16);

            const sorted = [...withSalesAndSpend].sort((a, b) => b.roi - a.roi);

            let totalSpend = 0;

            const formattedProducts = sorted.map(p => {

                const safeName = escapeHtml((p.name || `ID ${p.pid}`).slice(0, 25)).padEnd(25);
                const sold = String(p.sold).padStart(3);
                const spend = p.spend.toFixed(2).padStart(6);
                const avgPrice = p.sold > 0 ? (p.spend / p.sold).toFixed(2) : "0";

                totalSpend += p.spend;

                return `${safeName}\n${indent}${spend} / ${sold} = ${avgPrice}`;
            });

            const formattedOnlySpend = onlySpend.map(p => {
                const safeName = escapeHtml((p.name || `ID ${p.pid}`).slice(0, 25)).padEnd(25);
                const spend = p.spend.toFixed(2).padStart(6);
                return `${safeName} = $${spend}`;
            });

            const sumSpendRed = onlySpend
                .reduce((sum, p) => sum + p.spend, 0)
                .toFixed(2);

            const totalItems = withSalesAndSpend.reduce((sum, r) => sum + r.sold, 0);


            const avgItemsPerOrder =
                totalOrders > 0
                    ? (totalItems / totalOrders).toFixed(1)
                    : "0";

            const avgSpendPerOrder =
                totalOrders > 0
                    ? (totalSpend / totalOrders).toFixed(2)
                    : "0";

            const periodStart = dayjs(from).format("DD-MM");
            const periodEnd = dayjs(to).format("DD-MM");
            const sourceLabel =
                source === "site"
                    ? "🌐 Сайт"
                    : source === "ig"
                        ? "📱 Instagram"
                        : "📊 Всі";

            const baseMessage =
                `<b>LAVA test</b>\n` +
                `<b>Вигрузка ${periodStart} - ${periodEnd}</b>\n` +
                `<b>Джерело:</b> ${sourceLabel}\n\n` +
                `Речей у одному продажу: ${avgItemsPerOrder}\n` +
                `Вартість продажу за період: $${avgSpendPerOrder}\n\n`;


            let currentMessage = baseMessage + `<pre>`;
            let parts = [];

            for (let line of formattedProducts) {
                if (
                    (currentMessage +
                        line +
                        `</pre>\n\n<b>Бюджет:</b> $${totalSpend.toFixed(2)}\n<b>Кількість замовлень:</b> ${totalOrders}`
                    ).length > 4096
                ) {
                    currentMessage += `</pre>`;
                    parts.push(currentMessage);
                    currentMessage = `<pre>${line}\n`;
                } else {
                    currentMessage += line + "\n";
                }
            }

            currentMessage +=
                `</pre>\n\n` +
                `<b>Бюджет:</b> $${totalSpend.toFixed(2)}\n` +
                `<b>Кількість замовлень:</b> ${totalOrders}`;


            if (formattedOnlySpend.length > 0) {
                currentMessage +=
                    `\n\n<b>Злитий бюджет без продажів:</b> (всього $${sumSpendRed})\n` +
                    `<pre>${formattedOnlySpend.join("\n")}</pre>`;
            }

            parts.push(currentMessage);

            for (const part of parts) {

                const payload = {
                    chat_id: CHAT_ID,
                    parse_mode: "html",
                    text: part
                };

                const res = await fetch(
                    `https://api.telegram.org/${BOT_TOKEN}/sendMessage`,
                    {
                        method: "POST",
                        headers: { "Content-Type": "application/json" },
                        body: JSON.stringify(payload)
                    }
                );

                const data = await res.json();

                if (!res.ok || !data.result) {
                    console.error("❌ Telegram error:", data);
                    throw new Error("Помилка відправлення Telegram");
                }

                await fetch('/api/messages', {
                    method: "POST",
                    headers: { 'Content-Type': 'application/json;charset=utf-8' },
                    body: JSON.stringify({
                        message_id: data.result.message_id,
                        message: part,
                        date: new Date().toISOString()
                    })
                });
            }

            console.log("✅ Сообщение отправлено");

        } catch (error) {
            console.error("❌ Помилка під час відправлення:", error);
        }
    };
    const loadCatalogStats = async (ad) => {
        if (openedCatalogAdId === ad.ad_id) {
            setOpenedCatalogAdId(null);
            setCatalogStats([]);
            return;
        }

        try {
            const res = await axios.get("/api/fb/catalog-stats", {
                params: {
                    catalog_id: ad.ad_id,
                    from: dayjs(from).format("YYYY-MM-DD"),
                    to: dayjs(to).format("YYYY-MM-DD"),
                    source,
                },
            });

            setCatalogStats(res.data);
            setOpenedCatalogAdId(ad.ad_id);
        } catch (e) {
            console.error("Помилка catalog-stats", e);
            alert("Помилка отримання статистики каталогу");
        }
    };

    function buildAdsManagerLink(ad) {
        const actId = FB_ACCOUNTS[ad.source]; // ← берем из source

        const BUSINESS_ID = "7126470020720395";
        const GLOBAL_SCOPE_ID = "7126470020720395";

        const fromDate = dayjs(from).format("YYYY-MM-DD");
        const toDate = dayjs(to).add(1, "day").format("YYYY-MM-DD");

        return `https://adsmanager.facebook.com/adsmanager/manage/ads/insights?act=${actId}&business_id=${BUSINESS_ID}&global_scope_id=${GLOBAL_SCOPE_ID}&date=${fromDate}_${toDate}&insights_date=${fromDate}_${toDate}&selected_ad_ids=${ad.ad_id}`;
    }






    const handleThumbnailClick = async (ad) => {

        // FB VIDEO → открыть facebook
        if (ad.media_type === "fb_video") {
            if (ad.facebook_video_url) {
                window.open(ad.facebook_video_url, "_blank");
            }
            return;
        }

        // IG CAROUSEL → модалка со слайдером
        if (ad.media_type === "carousel") {
            setModalContent({ ...ad, carousel: ad.carousel || [], slide: 0 });
            new bootstrap.Modal(document.getElementById("creativeModal")).show();
            return;
        }

        // Каталог
        if (ad.media_type === "catalog") {
            await loadCatalogStats(ad);
            return;
        }

        // IMAGE / VIDEO
        setModalContent({ ...ad, bigImage: null });
        const modal = new bootstrap.Modal(document.getElementById("creativeModal"));
        modal.show();

        setTimeout(async () => {
            try {
                if (ad.creative_id) {
                    const res = await axios.get(`/api/fb/creative/${ad.creative_id}`);
                    if (res.data.bigImage) {
                        setModalContent(prev => ({
                            ...prev,
                            bigImage: res.data.bigImage,
                        }));
                    }
                }
            } catch (e) {
                console.error("BIG IMAGE LOAD ERROR", e);
            }
        }, 50);
    };


    const renderCreativeTable = (pid) => {

        const ads = creativesByProduct[pid] || [];

        if (!ads.length) {
            return <div className="text-muted">Немає креативів</div>;
        }

        return (
            <table className="table table-sm align-middle products-matrix-creatives-table">
                <thead>
                <tr>
                    <th>Креатив</th>
                    <th>Назва</th>
                    <th>Аккаунт</th>
                    <th>ID</th>
                    <th>Статус</th>
                    <th className="text-end">Бюджет</th>
                    <th className="text-end">Кліки</th>
                    <th className="text-end">Покази</th>
                    <th className="text-end">CPM</th>
                    <th className="text-end">CPC</th>
                    <th className="text-end">CTR</th>
                </tr>
                </thead>

                <tbody>
                {ads.map(ad => {

                    const productsCount =
                        ad.selected_products?.length || 1;

                    const spend =
                        (ad.spend || 0) / productsCount;

                    const clicks =
                        (ad.clicks || 0) / productsCount;

                    const impressions =
                        (ad.impressions || 0) / productsCount;

                    const cpm =
                        impressions > 0
                            ? (spend / impressions) * 1000
                            : 0;

                    const cpc =
                        clicks > 0
                            ? spend / clicks
                            : 0;

                    const ctr =
                        impressions > 0
                            ? (clicks / impressions) * 100
                            : 0;

                    const accountLabel =
                        ACCOUNTS[ad.source] || ad.source;

                    const thumbSrc = creativeTableThumbnailSrc(ad);

                    return (
                        <tr key={ad.ad_id}>

                            {/* Thumbnail */}
                            <td className="td-thumbnail">
                                <div
                                    style={{ width: 70, height: 70 }}
                                    className="position-relative rounded overflow-hidden cursor-pointer bg-secondary bg-opacity-25"
                                    onClick={() => handleThumbnailClick(ad)}
                                >

                                    {thumbSrc ? (
                                        <img
                                            referrerPolicy="no-referrer"
                                            loading="eager"
                                            src={thumbSrc}
                                            className="w-100 h-100"
                                            style={{ objectFit: "cover" }}
                                            alt=""
                                            onError={(e) => {
                                                e.currentTarget.style.display = "none";
                                            }}
                                        />
                                    ) : null}

                                    {ad.is_catalog && (
                                        <div className="position-absolute top-0 end-0 text-white px-1 small rounded">
                                            🛒
                                        </div>
                                    )}

                                    {(ad.media_type === "video" ||
                                        ad.media_type === "fb_video") && (
                                        <div className="position-absolute top-50 start-50 translate-middle">
                                            <div
                                                className="bg-dark bg-opacity-75 rounded-circle color-white d-flex justify-content-center align-items-center"
                                                style={{ width: 34, height: 34, color: "#fff", fontSize: 8 }}
                                            >
                                                ▶
                                            </div>
                                        </div>
                                    )}
                                </div>
                                <span className="products-matrix-creative-type" title={ad.media_type}>
                                    {ad.media_type}
                                </span>
                            </td>




                            {/* Название + продукты */}
                            <td>
                                <div className=" small">
                                    {ad.ad_name}
                                </div>

                                <div className="mt-1">
                                    {ad.selected_products?.map(p => (
                                        <span
                                            key={p}
                                            className="badge bg-primary me-1"
                                        >
                                            {names[p] || p}
                                        </span>
                                    ))}
                                </div>
                            </td>

                            <td>{accountLabel}</td>
                            <td>
                                <a
                                    href={buildAdsManagerLink(ad)}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-decoration-none small"
                                >
                                    {ad.ad_id}
                                </a>
                            </td>

                            <td>
                                <MatrixCreativeStatus ad={ad} />
                            </td>

                            <td className="text-end">
                                ${spend.toFixed(2)}
                            </td>

                            <td className="text-end">
                                {Math.round(clicks)}
                            </td>

                            <td className="text-end">
                                {Math.round(impressions)}
                            </td>

                            <td className="text-end">
                                {cpm.toFixed(2)}
                            </td>

                            <td className="text-end">
                                {cpc.toFixed(2)}
                            </td>

                            <td className="text-end">
                                {ctr.toFixed(2)}%
                            </td>

                        </tr>
                    );
                })}
                </tbody>
            </table>
        );
    };
    const sortedPerformance = React.useMemo(() => {

        const arr = [...withSalesAndSpend];

        arr.sort((a, b) => {

            let aVal;
            let bVal;

            // 🔹 сортировка по имени
            if (tableSortField === "name") {

                const aName = a.name || "";
                const bName = b.name || "";

                return tableSortDir === "asc"
                    ? aName.localeCompare(bName)
                    : bName.localeCompare(aName);
            }

            // 🔹 сортировка по вычисляемой цене продажи (spend / sold)
            if (tableSortField === "saleCost") {

                aVal = a.sold > 0 ? a.spend / a.sold : 0;
                bVal = b.sold > 0 ? b.spend / b.sold : 0;
            }
            else {
                aVal = a[tableSortField] || 0;
                bVal = b[tableSortField] || 0;
            }

            return tableSortDir === "asc"
                ? aVal - bVal
                : bVal - aVal;
        });

        return arr;

    }, [withSalesAndSpend, tableSortField, tableSortDir]);


    const toggleTableSort = (field) => {

        if (tableSortField === field) {
            setTableSortDir(prev => prev === "asc" ? "desc" : "asc");
        } else {
            setTableSortField(field);
            setTableSortDir("desc");
        }
    };

    const footerTotals = React.useMemo(() => {

        const totalSold = sortedPerformance.reduce((s,r)=> s + r.sold, 0);
        const totalSpend = sortedPerformance.reduce((s,r)=> s + r.spend, 0);
        const totalRevenue = sortedPerformance.reduce((s,r)=> s + r.revenue, 0);
        const totalCost = sortedPerformance.reduce((s,r)=> s + r.cost, 0);
        const totalProfit = sortedPerformance.reduce((s,r)=> s + r.totalProfit, 0);

        const saleCost = totalSold > 0 ? totalSpend / totalSold : 0;
        const unitCost = totalSold > 0 ? totalRevenue / totalSold : 0;
        const unitMargin = totalSold > 0 ? (totalRevenue - totalCost) / totalSold : 0;
        const marginPercent = unitCost > 0
            ? (unitMargin / unitCost) * 100
            : 0;

        const unitProfit = totalSold > 0 ? totalProfit / totalSold : 0;
        const roi = totalSpend > 0 ? (totalProfit / totalSpend) * 100 : 0;
        const totalClicks = sortedPerformance.reduce((s,r)=> s + (r.clicks || 0), 0);
        const totalImpressions = sortedPerformance.reduce((s,r)=> s + (r.impressions || 0), 0);
        const cpm = totalImpressions > 0
            ? (totalSpend / totalImpressions) * 1000
            : 0;

        const cpc = totalClicks > 0
            ? totalSpend / totalClicks
            : 0;

        const ctr = totalImpressions > 0
            ? (totalClicks / totalImpressions) * 100
            : 0;


        return {
            totalSold,
            totalSpend,
            totalProfit,
            saleCost,
            unitCost,
            unitMargin,
            marginPercent,
            unitProfit,
            roi,
            cpm,
            cpc,
            ctr
        };

    }, [sortedPerformance]);




    return (
        <div className="container container-matrix products-matrix-page">
            <div className="products-matrix-hero d-lg-none">
                <h1 className="products-matrix-hero__title">Вигрузка</h1>
                <p className="products-matrix-hero__period">
                    {dayjs(from).format("DD.MM.YYYY")} — {dayjs(to).format("DD.MM.YYYY")}
                </p>
            </div>

            <div className="d-flex justify-content-start my-3 products-matrix-toolbar flex-wrap align-items-stretch">
                {isAdmin && (<><DatePicker
                    selected={from}
                    onChange={setFrom}
                    dateFormat="dd/MM/yyyy"
                    locale="uk"
                    placeholderText="Оберіть дату"
                />
                    <DatePicker
                    selected={to}
                onChange={setTo}
                dateFormat="dd/MM/yyyy"
                locale="uk"
                placeholderText="Оберіть дату"
            />
            <button className="btn btn-primary" onClick={load}>Оновити</button>
            </>)
            }
                <button
                    className={`btn ${
                        activePeriod === "3days"
                            ? "btn-secondary"
                            : "btn-outline-secondary"
                    }`}
                    onClick={() => {
                        setLastDays(3);
                        setActivePeriod("3days");
                    }}
                >
                    Останні 3 дні
                </button>
                <button
                    className={`btn ${
                        activePeriod === "7days"
                            ? "btn-secondary"
                            : "btn-outline-secondary"
                    }`}
                    onClick={() => {
                        setLastDays(7);
                        setActivePeriod("7days");
                    }}
                >
                    Останні 7 днів
                </button>
                <button
                    className={`btn ${
                        activePeriod === "currentMonth"
                            ? "btn-secondary"
                            : "btn-outline-secondary"
                    }`}
                    onClick={() => {
                        const m = dayjs().tz(TIMEZONE);
                        setFrom(m.startOf("month").toDate());
                        setTo(m.endOf("month").toDate());
                        setActivePeriod("currentMonth");
                    }}
                >
                    Поточний місяць
                </button>
                <button
                    className={`btn ${
                        activePeriod === "prevMonth"
                            ? "btn-secondary"
                            : "btn-outline-secondary"
                    }`}
                    onClick={() => {
                        const m = dayjs().tz(TIMEZONE).subtract(1, "month");
                        setFrom(m.startOf("month").toDate());
                        setTo(m.endOf("month").toDate());
                        setActivePeriod("prevMonth");
                    }}
                >
                    Попередній місяць
                </button>
            </div>
            <div className="products-matrix-filters">
                <div className="products-matrix-filters__toolbar">
                    <div className="products-matrix-filters__group products-matrix-filters__group--source">
                        <label className="products-matrix-filters__label" htmlFor="matrix-filter-source-main">
                            Джерело
                        </label>
                        <select
                            id="matrix-filter-source-main"
                            className="form-select products-matrix-filters__control"
                            value={source}
                            onChange={e => setSource(e.target.value)}
                        >
                            <option value="all">Всі джерела</option>
                            <option value="site">Сайт</option>
                            <option value="ig">Instagram</option>
                        </select>
                    </div>
                    <div className="products-matrix-filters__group products-matrix-filters__group--currency">
                        <span className="products-matrix-filters__label">Валюта</span>
                        <div
                            className="products-matrix-currency-toggle products-matrix-currency-toggle--toolbar"
                            role="group"
                            aria-label="Валюта відображення"
                        >
                            <button
                                type="button"
                                className={`btn ${currency === "USD" ? "btn-dark" : "btn-outline-dark"}`}
                                onClick={() => setCurrency("USD")}
                            >
                                USD
                            </button>
                            <button
                                type="button"
                                className={`btn ${currency === "UAH" ? "btn-dark" : "btn-outline-dark"}`}
                                onClick={() => setCurrency("UAH")}
                            >
                                UAH
                            </button>
                        </div>
                    </div>
                    <div className="products-matrix-filters__group products-matrix-filters__group--sync">
                        <span className="products-matrix-filters__label products-matrix-filters__label--placeholder" aria-hidden="true">
                            &nbsp;
                        </span>
                        <button
                            type="button"
                            className="btn btn-outline-primary products-matrix-filters__sync-btn"
                            disabled={monthSheetSyncing}
                            onClick={() => void syncGoogleSheetMonthData()}
                            title="Поточний календарний місяць (Kyiv): усі дні з БД на лист, потім рядок сьогодні з live CRM/FB"
                        >
                            {monthSheetSyncing ? "…" : "Оновити місяць"}
                        </button>
                    </div>
                </div>
            </div>
            {loading || !matrix.length ? (
                <div className="d-flex justify-content-center align-items-center py-5">
                    <div className="spinner-border text-dark" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </div>
                </div>
            ) : (
                <>
            <section className="pt-3">
                <h3 className="products-matrix-section__title">
                    1️⃣ Окупність ({dayjs(from).format("DD-MM")} – {dayjs(to).format("DD-MM")})
                </h3>

                <div className="d-lg-none products-matrix-mobile-list">
                    {sortedPerformance.map((r, index) => {
                        const isOpen = expandedRows[r.pid];
                        const creativesCount = creativesByProduct[r.pid]?.length || 0;
                        return (
                            <div key={r.pid} className="products-matrix-mobile-card">
                                <button
                                    type="button"
                                    className="products-matrix-mobile-card__header"
                                    onClick={() => toggleRow(r.pid)}
                                >
                                    <span className="products-matrix-mobile-card__idx">{index + 1}</span>
                                    <div className="products-matrix-mobile-card__title-wrap">
                                        <span className="products-matrix-mobile-card__chevron">
                                            {isOpen ? "▼" : "▶"}
                                        </span>
                                        <span className="products-matrix-mobile-card__name">{r.name}</span>
                                        {creativesCount > 0 && (
                                            <span className="products-matrix-mobile-card__badge">
                                                ({creativesCount})
                                            </span>
                                        )}
                                    </div>
                                    <span
                                        className={`products-matrix-mobile-card__roi ${
                                            r.roi < 0 ? "text-danger" : "text-success"
                                        }`}
                                    >
                                        {r.roi.toFixed(1)}%
                                    </span>
                                </button>
                                <div className="products-matrix-mobile-card__grid">
                                    <div className="products-matrix-mobile-card__metric">
                                        <span className="products-matrix-mobile-card__label">Продано</span>
                                        <span className="products-matrix-mobile-card__value">{r.sold}</span>
                                    </div>
                                    <div className="products-matrix-mobile-card__metric">
                                        <span className="products-matrix-mobile-card__label">Бюджет</span>
                                        <span className="products-matrix-mobile-card__value">
                                            {formatMoneySimple(r.spend)}
                                        </span>
                                    </div>
                                    <div className="products-matrix-mobile-card__metric">
                                        <span className="products-matrix-mobile-card__label">Профіт</span>
                                        <span
                                            className={`products-matrix-mobile-card__value ${
                                                r.totalProfit < 0 ? "text-danger" : "text-success"
                                            }`}
                                        >
                                            {formatMoneySimple(r.totalProfit)}
                                        </span>
                                    </div>
                                    <div className="products-matrix-mobile-card__metric">
                                        <span className="products-matrix-mobile-card__label">Профіт/шт</span>
                                        <span
                                            className={`products-matrix-mobile-card__value ${
                                                r.unitProfit < 0 ? "text-danger" : "text-success"
                                            }`}
                                        >
                                            {formatMoneySimple(r.unitProfit)}
                                        </span>
                                    </div>
                                    <div className="products-matrix-mobile-card__metric">
                                        <span className="products-matrix-mobile-card__label">CPM</span>
                                        <span className="products-matrix-mobile-card__value">
                                            {r.cpm ? `$${r.cpm.toFixed(2)}` : "—"}
                                        </span>
                                    </div>
                                    <div className="products-matrix-mobile-card__metric">
                                        <span className="products-matrix-mobile-card__label">CPC · CTR</span>
                                        <span className="products-matrix-mobile-card__value">
                                            {r.cpc ? `$${r.cpc.toFixed(2)}` : "—"}
                                            {r.ctr ? ` · ${r.ctr.toFixed(1)}%` : ""}
                                        </span>
                                    </div>
                                </div>
                                {isOpen && (
                                    <div className="products-matrix-mobile-card__expand">
                                        <div className="table-responsive">
                                            {renderCreativeTable(r.pid)}
                                        </div>
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>

                <div className="d-lg-none products-matrix-mobile-summary">
                    <div className="products-matrix-mobile-summary__title">Підсумок за період</div>
                    <div className="products-matrix-mobile-summary__grid">
                        <div className="products-matrix-mobile-summary__item">
                            <span className="products-matrix-mobile-summary__label">ROI</span>
                            <span
                                className={`products-matrix-mobile-summary__value ${
                                    footerTotals.roi < 0 ? "text-danger" : "text-success"
                                }`}
                            >
                                {footerTotals.roi.toFixed(1)}%
                            </span>
                        </div>
                        <div className="products-matrix-mobile-summary__item">
                            <span className="products-matrix-mobile-summary__label">Продано шт.</span>
                            <span className="products-matrix-mobile-summary__value">
                                {footerTotals.totalSold}
                            </span>
                        </div>
                        <div className="products-matrix-mobile-summary__item">
                            <span className="products-matrix-mobile-summary__label">Бюджет</span>
                            <span className="products-matrix-mobile-summary__value">
                                {formatMoneySimple(footerTotals.totalSpend)}
                            </span>
                        </div>
                        <div className="products-matrix-mobile-summary__item">
                            <span className="products-matrix-mobile-summary__label">Заг. профіт</span>
                            <span
                                className={`products-matrix-mobile-summary__value ${
                                    footerTotals.totalProfit < 0 ? "text-danger" : "text-success"
                                }`}
                            >
                                {formatMoneySimple(footerTotals.totalProfit)}
                            </span>
                        </div>
                    </div>
                </div>

                <div className="d-none d-lg-block products-matrix-desktop-wrap">
                <div className="table-holder">
                    <table className="table table-bordered table-hover table-striped table-sticky table-light products-matrix-table products-matrix-table--roi">

                        <thead className="table-dark">
                        <tr>
                            <th style={{width:50}}>№</th>
                            <th onClick={() => toggleTableSort("name")} style={{cursor:"pointer"}}>
                                Товар {renderSortArrow("name")}
                            </th>

                            <th onClick={() => toggleTableSort("roi")} style={{cursor:"pointer"}}>
                                ROI {renderSortArrow("roi")}
                            </th>

                            <th onClick={() => toggleTableSort("sold")} style={{cursor:"pointer"}}>
                                Продано{renderSortArrow("sold")}
                            </th>
                            <th onClick={() => toggleTableSort("spend")} style={{cursor:"pointer"}}>
                                Бюджет {renderSortArrow("spend")}
                            </th>
                            <th onClick={() => toggleTableSort("cpm")} style={{cursor:"pointer"}}>
                                CPM {renderSortArrow("cpm")}
                            </th>

                            <th onClick={() => toggleTableSort("cpc")} style={{cursor:"pointer"}}>
                                CPC {renderSortArrow("cpc")}
                            </th>

                            <th onClick={() => toggleTableSort("ctr")} style={{cursor:"pointer"}}>
                                CTR {renderSortArrow("ctr")}
                            </th>
                            <th onClick={() => toggleTableSort("saleCost")} style={{cursor:"pointer"}}>
                                Вартість продажу {renderSortArrow("saleCost")}
                            </th>
                            <th onClick={() => toggleTableSort("avgSellPrice")} style={{cursor:"pointer"}}>
                                Вартість/шт {renderSortArrow("avgSellPrice")}
                            </th>
                            <th onClick={() => toggleTableSort("unitMargin")} style={{cursor:"pointer"}}>
                                Маржа/шт {renderSortArrow("unitMargin")}
                            </th>
                            <th onClick={() => toggleTableSort("marginPercent")} style={{cursor:"pointer"}}>
                                Маржинальність, % {renderSortArrow("marginPercent")}
                            </th>
                            <th onClick={() => toggleTableSort("unitProfit")} style={{cursor:"pointer"}}>
                                Профіт/шт {renderSortArrow("unitProfit")}
                            </th>

                            <th onClick={() => toggleTableSort("totalProfit")} style={{cursor:"pointer"}}>
                                Загальний профіт {renderSortArrow("totalProfit")}
                            </th>
                        </tr>
                        </thead>

                        <tbody>
                        {sortedPerformance.map((r, index) => {
                            const isOpen = expandedRows[r.pid];

                            return (
                                <React.Fragment key={r.pid}>

                                    {/* ====== ОСНОВНАЯ СТРОКА ТОВАРА ====== */}
                                    <tr
                                        style={{ cursor: "pointer" }}
                                        onClick={() => toggleRow(r.pid)}
                                        className=""
                                    >
                                        <td>{index + 1}</td>
                                        <td className="text-start px-2 products-matrix-table--roi__name">
                                            <span className="products-matrix-table--roi__toggle" aria-hidden>{isOpen ? "▼" : "▶"}</span>{" "}
                                            {r.name}
                                            {creativesByProduct[r.pid]?.length > 0 && (
                                                <span className="text-muted ms-1">
                                                ({creativesByProduct[r.pid].length})
                                            </span>
                                            )}
                                        </td>


                                        <td style={{color: r.roi < 0 ? "#dc3545" : "#198754"}}>
                                            {r.roi.toFixed(1)}%
                                        </td>

                                        <td>{r.sold}</td>
                                        <td>{formatMoneySimple(r.spend)}</td>
                                        <td>{r.cpm ? `$${r.cpm.toFixed(2)}` : ""}</td>
                                        <td>{r.cpc ? `$${r.cpc.toFixed(2)}` : ""}</td>
                                        <td>{r.ctr ? r.ctr.toFixed(2) + "%" : ""}</td>
                                        <td>
                                            {r.sold > 0
                                                ? formatMoneySimple(r.spend / r.sold)
                                                : ""}
                                        </td>
                                        <td>{formatMoneySimple(r.avgSellPrice)}</td>
                                        <td>{formatMoneySimple(r.unitMargin)}</td>
                                        <td style={{color: r.marginPercent < 0 ? "#dc3545" : "#198754"}}>
                                            {r.marginPercent ? r.marginPercent.toFixed(1) + "%" : ""}
                                        </td>
                                        <td style={{color: r.unitProfit < 0 ? "#dc3545" : "#198754"}}>
                                            {formatMoneySimple(r.unitProfit)}
                                        </td>

                                        <td style={{color: r.totalProfit < 0 ? "#dc3545" : "#198754"}}>
                                            {formatMoneySimple(r.totalProfit)}
                                        </td>
                                    </tr>
                                    {isOpen && (
                                        <tr className="products-matrix-table__expand-row">
                                            <td colSpan={14} className="p-0 border-0">
                                                <div className="products-matrix-table__expand-inner">
                                                    {renderCreativeTable(r.pid)}
                                                </div>
                                            </td>
                                        </tr>
                                    )}


                                </React.Fragment>
                            );
                        })}
                        </tbody>
                        <tfoot className="table-dark fw-bold">
                        <tr>
                            <td></td>
                            <td>Всього</td>

                            <td>
                                {footerTotals.roi.toFixed(1)}%
                            </td>

                            <td>{footerTotals.totalSold}</td>

                            <td>{formatMoneySimple(footerTotals.totalSpend)}</td>
                            <td>{footerTotals.cpm.toFixed(2)}</td>
                            <td>{footerTotals.cpc.toFixed(2)}</td>
                            <td>{footerTotals.ctr.toFixed(2)}%</td>

                            <td>{formatMoneySimple(footerTotals.saleCost)}</td>

                            <td>{formatMoneySimple(footerTotals.unitCost)}</td>

                            <td>{formatMoneySimple(footerTotals.unitMargin)}</td>
                            <td>
                                {footerTotals.marginPercent.toFixed(1)}%
                            </td>
                            <td>
                                {formatMoneySimple(footerTotals.unitProfit)}
                            </td>

                            <td>
                                {formatMoneySimple(footerTotals.totalProfit)}
                            </td>
                        </tr>
                        </tfoot>
                    </table>
                </div>
                </div>
            </section>
            <section className="pt-3">
                <h3 className="products-matrix-section__title">
                    <span>
                        2️⃣ Вигрузка ({dayjs(from).format("DD-MM")} – {dayjs(to).format("DD-MM")})
                    </span>
                    <button
                        type="button"
                        className="btn btn-sm btn-primary"
                        onClick={sendToTelegram}
                    >
                        📤 Надіслати
                    </button>
                </h3>

                <div className="products-matrix-export-cols mt-4">

                    {/* Продажи с рекламой */}
                    <div>
                        <h6>
                            Продажі з рекламою [ бюджет {formatMoneyDynamic(
                            withSalesAndSpend.reduce((sum, r) => sum + r.spend, 0)
                        )} ]
                        </h6>

                        <ul className="list-group">
                            {withSalesAndSpend.sort((a,b)=> b.roi - a.roi).map(r => (
                                <li key={r.pid} className="list-group-item border-0 mb-0 products-matrix-export-item font-size-sm">
                                    {r.name}:
                                    {formatMoneyDynamic(r.spend)} / {r.sold} =
                                    {r.sold > 0 ? formatMoneyDynamic(r.spend / r.sold) : ""}
                                </li>
                            ))}
                        </ul>
                    </div>

                    {/* Продажи без рекламы */}
                    <div>
                        <h6>
                            Продажі без реклами [ кількість {onlySales.reduce((sum, r) => sum + r.sold, 0)} шт. ]
                        </h6>

                        <ul className="list-group">
                            {onlySales.map(r => (
                                <li key={r.pid} className="list-group-item border-0 mb-0">
                                    {r.name}: {r.sold} шт.
                                </li>
                            ))}
                        </ul>
                    </div>

                    {/* Слитый бюджет */}
                    <div>
                        <h6>
                            Злитий бюджет без продажів [ бюджет {formatMoneyDynamic(
                            onlySpend.reduce((sum, r) => sum + r.spend, 0)
                        )} ]
                        </h6>

                        <ul className="list-group">
                            {onlySpend.map(r => (
                                <li key={r.pid} className="list-group-item border-0 mb-0">
                                    {r.name}: {formatMoneyDynamic(r.spend)}
                                </li>
                            ))}
                        </ul>
                    </div>

                </div>
            </section>

            <section className="pt-3">
                <h3 className="products-matrix-section__title">📊 Вигрузка товарів по дням</h3>
                <p className="text-muted small d-lg-none mb-2">
                    Горизонтальний скрол — змахніть таблицю вбік, щоб побачити всі дні.
                </p>
                <div className="products-matrix-filters mb-3">
                    <div className="products-matrix-filters__row products-matrix-filters__row--viewmode">
                        <label className="products-matrix-filters__label" htmlFor="matrix-filter-viewmode">
                            Відображення
                        </label>
                        <select
                            id="matrix-filter-viewmode"
                            className="form-select"
                            value={viewMode}
                            onChange={e => setViewMode(e.target.value)}
                        >
                            <option value="sold">Продажи</option>
                            <option value="revenue">Каса</option>
                            <option value="spent">Бюджет</option>
                            <option value="roas">ROAS</option>
                            <option value="cpa">CPA</option>
                            <option value="profit">Profit</option>
                        </select>
                    </div>
                    <div className="products-matrix-filters__row products-matrix-filters__row--source">
                        <label className="products-matrix-filters__label" htmlFor="matrix-filter-source-daily">
                            Джерело
                        </label>
                        <select
                            id="matrix-filter-source-daily"
                            className="form-select"
                            value={source}
                            onChange={e => setSource(e.target.value)}
                        >
                            <option value="all">Всі джерела</option>
                            <option value="site">Сайт</option>
                            <option value="ig">Instagram</option>
                        </select>
                    </div>
                </div>
                <div className="products-matrix-daily-wrap">
                <div className="products-matrix-desktop-wrap">
                <div className="table-holder">
                    <table className="table table-hover table-striped table-sticky table-light products-matrix-table products-matrix-table--daily">
                        <thead>
                        <tr className="text-center products-matrix-table--daily__head-main">
                            <th className="products-matrix-daily__col-idx">№</th>
                            <th className="products-matrix-daily__col-name" onClick={()=> toggleSort("name")} style={{cursor:"pointer"}}>
                                Товар
                                {sortField==="name" && (sortDir==="asc" ? " 🔼" : " 🔽")}
                            </th>
                            <th className="products-matrix-daily__col-total" onClick={()=>setSortDir(d => d==="asc"?"desc":"asc")}>
                                Total {viewMode.toUpperCase()} {sortDir==="asc"?"🔼":"🔽"}
                            </th>
                            {days.map(d=> (
                                <th key={d}
                                    onClick={() => toggleSort(d)}
                                    style={{ cursor:"pointer" }}
                                >
                                    {dayjs(d).format("DD MMM").replace(".", "")}
                                    {sortField===d && (sortDir==="asc" ? " 🔼" : " 🔽")}
                                </th>
                            ))}
                        </tr>
                        <tr className="text-center products-matrix-table--daily__head-totals">
                            <th className="products-matrix-daily__totals-label" colSpan={2}>ВСЬОГО</th>
                            <th className="products-matrix-daily__col-total">
                                {viewMode === "spent" && grandTotal
                                    ? formatMoney(grandTotal, "spend")
                                    : ""}
                                {viewMode === "profit" && grandTotal
                                    ? formatMoney(grandTotal, "profit")
                                    : ""}
                                {viewMode === "revenue" && grandTotal
                                    ? formatMoney(grandTotal, "revenue")
                                    : ""}
                                {(viewMode === "roas" || viewMode === "cpa") && grandTotal
                                    ? Number(grandTotal).toFixed(2)
                                    : ""}
                                {viewMode === "sold" ? grandTotal : ""}
                            </th>
                            {dayTotals.map((total, i) => (
                                <th key={i}>
                                    {viewMode === "spent" && total ? formatMoney(total, "spend") : ""}
                                    {viewMode === "revenue" && total ? formatMoney(total, "revenue") : ""}
                                    {viewMode === "profit" && total ? formatMoney(total, "profit") : ""}
                                    {(viewMode === "roas" || viewMode === "cpa") && total
                                        ? Number(total).toFixed(2)
                                        : ""}
                                    {viewMode === "sold" ? total || "" : ""}
                                </th>
                            ))}
                        </tr>
                        </thead>
                        <tbody>
                        {sortedMatrix.filter(row => hasAnyDataBySource(row, source)).map((row,idx)=>{
                            const allZero = row.days.every(d =>
                                pick(d, "qty") === 0 &&
                                pick(d, "spend") === 0 &&
                                pick(d, "revenue") === 0
                            );

                            if(allZero) return null;

                            const totalSpent = row.days.reduce((s,x)=>s + pick(x, "spend"), 0);
                            const totalSold  = row.days.reduce((s,x)=>s + pick(x, "qty"), 0);
                            const totalRevenueUSD = row.days.reduce(
                                (s,x)=> s + revenueUSD(x), 0
                            );
                            const totalCost = row.days.reduce(
                                (s,x)=>s + pick(x, "cost"), 0
                            );

                            const totalProfit = totalRevenueUSD - totalCost - totalSpent;

                            const totalROAS = totalSpent>0 ? totalRevenueUSD/totalSpent : 0;
                            const totalCPA = totalSold>0  ? totalSpent/totalSold  : 0;

                            return (
                                <tr key={row.pid}>
                                    <td className="products-matrix-daily__col-idx">{idx + 1}</td>
                                    <td className="fw-bold products-matrix-daily__col-name">{row.name ? `${row.name} {${row.pid}}` : `ID ${row.pid}`}</td>
                                    <td className="fw-bold products-matrix-daily__col-total">
                                        {viewMode==="sold" && <>
                                            🧾 {totalSold}
                                        </>}
                                        {viewMode==="spent" && <>
                                            💸 {formatMoney(totalSpent, "spend")}
                                        </>}
                                        {viewMode==="revenue" && <>
                                            {formatMoney(totalRevenueUSD, "revenue")}
                                        </>}
                                        {viewMode==="roas" && <>
                                            {totalROAS.toFixed(2)}
                                        </>}
                                        {viewMode==="profit" && <>
                                            🟢 {formatMoney(totalProfit, "profit")}
                                        </>}
                                        {viewMode==="cpa" && <>
                                            {totalCPA.toFixed(2)}
                                        </>}
                                    </td>
                                    {row.days.map((x, i) => {

                                        if (viewMode === "profit") {
                                            const spent = pick(x, "spend");
                                            const revUSD = revenueUSD(x);
                                            const costUSD = pick(x, "cost");
                                            const profitUSD = revUSD - costUSD - spent;

                                            return (
                                                <td
                                                    key={i}
                                                    style={{
                                                        color: profitUSD < 0 ? "#dc3545" : "#198754",
                                                        fontWeight: 500
                                                    }}
                                                >
                                                    {profitUSD ? formatMoney(profitUSD, "profit") : ""}
                                                </td>
                                            );
                                        }

                                        return <td key={i}>{formatCell(x)}</td>;
                                    })}
                                </tr>
                            )
                        })}
                        </tbody>
                    </table>
                </div>
                </div>
                </div>
            </section>
            </>)}
            <div className="modal fade" id="creativeModal" tabIndex="-1">
                <div className="modal-dialog modal-dialog-centered modal-lg">
                    <div className="modal-content">
                        <div className="modal-header">
                            <h5 className="modal-title">{modalContent?.media_type}</h5>
                            <button
                                type="button"
                                className="btn-close"
                                data-bs-dismiss="modal"
                                aria-label="Close"
                            />
                        </div>

                        <div className="modal-body text-center">

                            {/* IMAGE */}
                            {modalContent?.media_type === "image" && (
                                <img
                                    src={modalContent.bigImage || modalContent.thumbnail_url}
                                    className="img-fluid rounded"
                                    style={{ maxHeight: "75vh" }}
                                    alt=""
                                />
                            )}

                            {/* VIDEO */}
                            {modalContent?.media_type === "video" && (
                                <video
                                    src={modalContent.media_url}
                                    controls
                                    autoPlay
                                    muted
                                    playsInline
                                    className="w-100 rounded"
                                    style={{ maxHeight: "75vh" }}
                                />
                            )}

                            {/* CAROUSEL */}
                            {modalContent?.media_type === "carousel" &&
                                modalContent.carousel?.length > 0 && (() => {

                                    const slide = modalContent.slide ?? 0;
                                    const item = modalContent.carousel[slide];

                                    return (
                                        <img
                                            src={item.media_url || item.thumbnail_url}
                                            className="img-fluid rounded"
                                            style={{ maxHeight: "75vh" }}
                                            alt=""
                                        />
                                    );
                                })()}

                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}
